import { useState, useRef, useEffect } from 'react'
import { Send, Loader2, Sparkles } from 'lucide-react'
import { useProcessorStore } from '../stores/processorStore'

const EXAMPLE_PROMPTS = [
  'Summarize this document',
  'What are the key points?',
  'Extract all dates and numbers',
  'What is the main topic?',
  'List any action items or recommendations',
]

export function PromptInput() {
  const [prompt, setPrompt] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const {
    uploadedFile,
    isProcessing,
    selectedModel,
    addMessage,
    setIsProcessing,
    setProcessingError,
  } = useProcessorStore()

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }
  }, [prompt])

  const handleSubmit = async () => {
    if (!prompt.trim() || !uploadedFile || isProcessing) return

    const userPrompt = prompt.trim()
    setPrompt('')
    addMessage({ role: 'user', content: userPrompt })
    addMessage({ role: 'assistant', content: '' }) // Placeholder for streaming
    setIsProcessing(true)
    setProcessingError(null)

    try {
      const endpoint = uploadedFile.type === 'image' ? '/api/analyze-image' : '/api/chat'

      const body =
        uploadedFile.type === 'image'
          ? {
              prompt: userPrompt,
              imageBase64: uploadedFile.imageBase64,
              model: selectedModel,
            }
          : {
              prompt: userPrompt,
              pdfText: uploadedFile.pdfText,
              pdfBase64: uploadedFile.pdfBase64, // For native PDF vision
              pageImages: uploadedFile.pageImages, // Rendered page images for vision fallback
              model: selectedModel,
            }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const error = await response.json()
          errorMessage = error.message || error.error || errorMessage
        } catch {
          // Response wasn't JSON, try text
          try {
            errorMessage = await response.text()
          } catch {
            // Couldn't read response
          }
        }
        throw new Error(errorMessage)
      }

      // Handle streaming response
      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let fullContent = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') continue

            try {
              const parsed = JSON.parse(data)
              const content = parsed.choices?.[0]?.delta?.content || ''
              if (content) {
                fullContent += content
                useProcessorStore.getState().updateLastAssistantMessage(fullContent)
              }
            } catch {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      setIsProcessing(false)
    } catch (err) {
      setProcessingError(err instanceof Error ? err.message : 'Request failed')
      // Remove the empty assistant message on error
      useProcessorStore.setState((state) => ({
        messages: state.messages.slice(0, -1),
      }))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleExampleClick = (example: string) => {
    setPrompt(example)
    textareaRef.current?.focus()
  }

  const isDisabled = !uploadedFile || isProcessing

  return (
    <div className="space-y-3">
      {/* Example prompts */}
      {uploadedFile && !isProcessing && (
        <div className="flex flex-wrap gap-2">
          {EXAMPLE_PROMPTS.map((example) => (
            <button
              key={example}
              onClick={() => handleExampleClick(example)}
              className="text-xs px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-full
                       text-gray-400 hover:text-gray-200 transition-colors flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              {example}
            </button>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            uploadedFile
              ? 'Ask a question about your document...'
              : 'Upload a file first...'
          }
          disabled={isDisabled}
          rows={1}
          className={`
            w-full bg-gray-800 rounded-lg px-4 py-3 pr-12 resize-none
            placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        />
        <button
          onClick={handleSubmit}
          disabled={isDisabled || !prompt.trim()}
          className={`
            absolute right-2 bottom-2 p-2 rounded-lg transition-colors
            ${
              isDisabled || !prompt.trim()
                ? 'text-gray-600 cursor-not-allowed'
                : 'text-blue-400 hover:bg-gray-700'
            }
          `}
        >
          {isProcessing ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  )
}
